import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import InlineStatusSelect from './InlineStatusSelect';
import ORModal from './ORModal';
import './ReceptionVip.css';
import { PlanificationCell, useDossierPlanning } from './shared/DossierPlanification';

const STATUTS = [
  { valeur: 'recu',             label: 'Reçu',                    couleur: '#3b82f6' },
  { valeur: 'en_diagnostic',    label: 'En diagnostic',          couleur: '#f59e0b' },
  { valeur: 'pieces_commande',  label: 'Pièces en commande',     couleur: '#a855f7' },
  { valeur: 'attente_garantie', label: 'Attente retour garantie', couleur: '#06b6d4' },
  { valeur: 'attente_accord',   label: 'Attente accord client',  couleur: '#ec4899' },
  { valeur: 'pret',             label: 'Prêt',                   couleur: '#10b981' },
  { valeur: 'livre',            label: 'Livré',                  couleur: '#6b7280' },
];

function getStatut(valeur) {
  return STATUTS.find(s => s.valeur === valeur) || { valeur, label: valeur, couleur: '#94a3b8' };
}

function StatutBadge({ valeur }) {
  const s = getStatut(valeur);
  return (
    <span className="statut-badge" style={{ background: s.couleur }}>
      {s.label}
    </span>
  );
}

/* ─── Modal photos lightbox ──────────────────────── */
function Lightbox({ src, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="rv-lightbox-overlay" onClick={onClose}>
      <img src={`data:image/jpeg;base64,${src}`} alt="Photo agrandie" />
    </div>
  );
}

/* ─── Modal détail dossier ──────────────────────── */
function DossierModal({ dossier, onClose, onSaved, onDeleted, isSuperAdmin }) {
  const [photosData, setPhotosData] = useState(null);
  const [photosLoading, setPhotosLoading] = useState(true);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const [editClientUsername, setEditClientUsername] = useState(dossier.client_username || '');
  const [editStatut, setEditStatut] = useState(dossier.statut || 'recu');
  const [editDate, setEditDate] = useState(
    dossier.date_restitution ? dossier.date_restitution.substring(0, 10) : ''
  );
  const [editDateAction, setEditDateAction] = useState(
    dossier.date_prochaine_action ? dossier.date_prochaine_action.split('T')[0] : ''
  );
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Charger les photos séparément
  useEffect(() => {
    const load = async () => {
      try {
        const resp = await api.get(`/reception-vip/${dossier.id}/photos`);
        setPhotosData(resp.data);
      } catch {
        setPhotosData(null);
      } finally {
        setPhotosLoading(false);
      }
    };
    load();
  }, [dossier.id]);

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer définitivement le dossier #${dossier.id} (${dossier.client_username}) ?`)) return;
    setDeleting(true);
    try {
      await api.delete(`/reception-vip/${dossier.id}`);
      onDeleted(dossier.id);
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      await api.put(`/reception-vip/${dossier.id}`, {
        client_username: editClientUsername.trim(),
        statut: editStatut,
        date_restitution: editDate || null,
        date_prochaine_action: editDateAction || null,
      });
      onSaved({ ...dossier, client_username: editClientUsername.trim(), statut: editStatut, date_restitution: editDate || null, date_prochaine_action: editDateAction || null });
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const dateDepot = dossier.created_at
    ? new Date(dossier.created_at).toLocaleString('fr-FR')
    : '—';

  return (
    <>
      <div className="modal-overlay">
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="modal-header">
            <h2>
              🏁 Dossier #{dossier.id}{dossier.plaque ? ` — ${dossier.plaque}` : ''}
            </h2>
            <button className="modal-close" onClick={onClose} aria-label="Fermer">✕</button>
          </div>

          {/* Body */}
          <div className="modal-body">
            {/* Infos */}
            <div className="rv-info-grid">
              <div className="rv-info-item">
                <label>Client</label>
                <input
                  type="text"
                  value={editClientUsername}
                  onChange={e => setEditClientUsername(e.target.value)}
                  className="rv-inline-input"
                  placeholder="Nom du client"
                />
              </div>
              <div className="rv-info-item">
                <label>Plaque</label>
                <span>
                  {dossier.plaque
                    ? <span className="plaque-badge">{dossier.plaque}</span>
                    : '—'
                  }
                </span>
              </div>
              <div className="rv-info-item">
                <label>Statut actuel</label>
                <span><StatutBadge valeur={dossier.statut} /></span>
              </div>
              <div className="rv-info-item">
                <label>Date dépôt</label>
                <span>{dateDepot}</span>
              </div>
              {dossier.concession_nom && (
                <div className="rv-info-item">
                  <label>Concession</label>
                  <span>{dossier.concession_nom}</span>
                </div>
              )}
              {dossier.date_restitution && (
                <div className="rv-info-item">
                  <label>Date restitution</label>
                  <span>{new Date(dossier.date_restitution).toLocaleDateString('fr-FR')}</span>
                </div>
              )}
            </div>

            {/* Description */}
            {dossier.description && (
              <div className="rv-description-box">{dossier.description}</div>
            )}

            {/* Carte Google Maps */}
            {dossier.latitude && dossier.longitude && (
              <iframe
                className="rv-map-iframe"
                src={`https://maps.google.com/maps?q=${dossier.latitude},${dossier.longitude}&t=k&z=18&output=embed`}
                allowFullScreen
                loading="lazy"
                title="Géolocalisation"
              />
            )}

            {/* Photos */}
            {photosLoading ? (
              <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Chargement des photos…</p>
            ) : photosData && (photosData.photo_plaque || (photosData.photos_vehicule && photosData.photos_vehicule.length > 0)) ? (
              <div className="rv-photos-section">
                <h4>Photos</h4>
                <div className="rv-photos-grid">
                  {photosData.photo_plaque && (
                    <div
                      className="rv-photo-thumb"
                      title="Photo plaque"
                      onClick={() => setLightboxSrc(photosData.photo_plaque)}
                    >
                      <img src={`data:image/jpeg;base64,${photosData.photo_plaque}`} alt="Plaque" />
                    </div>
                  )}
                  {photosData.photos_vehicule && photosData.photos_vehicule.map((p, i) => (
                    <div
                      key={i}
                      className="rv-photo-thumb"
                      title={`Photo véhicule ${i + 1}`}
                      onClick={() => setLightboxSrc(p)}
                    >
                      <img src={`data:image/jpeg;base64,${p}`} alt={`Véhicule ${i + 1}`} />
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {/* Formulaire édition */}
            <div className="rv-edit-form">
              <h4>Mettre à jour le dossier</h4>
              <div className="rv-form-row">
                <div className="rv-form-group">
                  <label>Statut</label>
                  <select value={editStatut} onChange={e => setEditStatut(e.target.value)}>
                    {STATUTS.map(s => (
                      <option key={s.valeur} value={s.valeur}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div className="rv-form-group">
                  <label>Date de restitution</label>
                  <input
                    type="date"
                    value={editDate}
                    onChange={e => setEditDate(e.target.value)}
                  />
                </div>
                <div className="rv-form-group">
                  <label>📅 Prochaine action</label>
                  <input
                    type="date"
                    value={editDateAction}
                    onChange={e => setEditDateAction(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer">
            {saveError && <span className="rv-save-error">{saveError}</span>}
            {isSuperAdmin && (
              <button
                className="btn-danger"
                onClick={handleDelete}
                disabled={deleting || saving}
                style={{ marginRight: 'auto' }}
              >
                {deleting ? 'Suppression…' : '🗑 Supprimer'}
              </button>
            )}
            <button className="btn-secondary" onClick={onClose} disabled={saving || deleting}>
              Fermer
            </button>
            <button className="btn-primary" onClick={handleSave} disabled={saving || deleting}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>

      {lightboxSrc && <Lightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
    </>
  );
}

/* ─── Modale création manuelle ──────────────────── */
function CreateModal({ onClose, onCreated }) {
  const EMPTY = { client_username: '', plaque: '', num_or: '', description: '', statut: 'recu', date_restitution: '', date_prochaine_action: '' };
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_username.trim()) { setError('Le nom du client est requis'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form };
      if (!payload.date_restitution) delete payload.date_restitution;
      if (!payload.date_prochaine_action) delete payload.date_prochaine_action;
      const r = await api.post('/reception-vip/manual', payload);
      onCreated(r.data.dossier);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content small" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>➕ Nouveau dossier VIP</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit} className="modal-body">
          {error && <div style={{ background: '#fee2e2', color: 'var(--color-error-dark)', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>{error}</div>}

          <div className="rv-field-row">
            <label>Client <span style={{ color: 'var(--color-error)' }}>*</span></label>
            <input autoFocus value={form.client_username} onChange={e => set('client_username', e.target.value)} placeholder="Nom du client" />
          </div>
          <div className="rv-field-row">
            <label>Plaque</label>
            <input value={form.plaque} onChange={e => set('plaque', e.target.value)} placeholder="AB123CD" style={{ textTransform: 'uppercase' }} />
          </div>
          <div className="rv-field-row">
            <label>N° OR</label>
            <input value={form.num_or} onChange={e => set('num_or', e.target.value)} placeholder="OR-XXXXX" />
          </div>
          <div className="rv-field-row">
            <label>Statut</label>
            <select value={form.statut} onChange={e => set('statut', e.target.value)}>
              {STATUTS.map(s => <option key={s.valeur} value={s.valeur}>{s.label}</option>)}
            </select>
          </div>
          <div className="rv-field-row">
            <label>Date restitution prévue</label>
            <input type="date" value={form.date_restitution} onChange={e => set('date_restitution', e.target.value)} />
          </div>
          <div className="rv-field-row">
            <label>Prochaine action</label>
            <input type="date" value={form.date_prochaine_action} onChange={e => set('date_prochaine_action', e.target.value)} />
          </div>
          <div className="rv-field-row">
            <label>Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2} placeholder="Motif, remarques…" />
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>Annuler</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Création…' : 'Créer le dossier'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Modal création/édition client VIP ──────────── */
function ClientModal({ client, concessions, onClose, onSaved }) {
  const isEdit = !!client;
  const [form, setForm] = useState({
    username: client?.username || '',
    password: '',
    nom: client?.nom || '',
    prenom: client?.prenom || '',
    societe: client?.societe || '',
    email: client?.email || '',
    telephone: client?.telephone || '',
    concession_id: client?.concession_id || '',
    actif: client?.actif !== undefined ? client.actif : true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.username.trim()) { setError("L'identifiant est requis"); return; }
    if (!isEdit && (!form.password || form.password.length < 6)) { setError('Le mot de passe doit contenir au moins 6 caractères'); return; }
    if (!form.nom.trim()) { setError('Le nom est requis'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form };
      payload.concession_id = payload.concession_id ? parseInt(payload.concession_id) : null;
      if (isEdit && !payload.password) delete payload.password;

      let res;
      if (isEdit) {
        res = await api.put(`/reception-vip/clients/${client.id}`, payload);
      } else {
        res = await api.post('/reception-vip/clients', payload);
      }
      onSaved(res.data.client, isEdit);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content small" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? '✏️ Modifier le client' : '➕ Nouveau client VIP'}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit} className="modal-body">
          {error && <div style={{ background: '#fee2e2', color: 'var(--color-error-dark)', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>{error}</div>}

          <div className="rv-field-row">
            <label>Identifiant <span style={{ color: 'var(--color-error)' }}>*</span></label>
            <input
              autoFocus={!isEdit}
              value={form.username}
              onChange={e => set('username', e.target.value)}
              placeholder="jean.dupont"
              disabled={isEdit}
              style={isEdit ? { background: '#f3f4f6', color: 'var(--text-secondary)' } : {}}
            />
          </div>
          <div className="rv-field-row">
            <label>
              Mot de passe {!isEdit && <span style={{ color: 'var(--color-error)' }}>*</span>}
              {isEdit && <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>(laisser vide pour ne pas changer)</span>}
            </label>
            <input
              type="password"
              value={form.password}
              onChange={e => set('password', e.target.value)}
              placeholder={isEdit ? '••••••' : 'Min. 6 caractères'}
            />
          </div>
          <div className="rv-field-row">
            <label>Nom <span style={{ color: 'var(--color-error)' }}>*</span></label>
            <input value={form.nom} onChange={e => set('nom', e.target.value)} placeholder="Dupont" />
          </div>
          <div className="rv-field-row">
            <label>Prénom</label>
            <input value={form.prenom} onChange={e => set('prenom', e.target.value)} placeholder="Jean" />
          </div>
          <div className="rv-field-row">
            <label>Email</label>
            <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="jean.dupont@email.com" />
          </div>
          <div className="rv-field-row">
            <label>Téléphone</label>
            <input value={form.telephone} onChange={e => set('telephone', e.target.value)} placeholder="06 12 34 56 78" />
          </div>
          <div className="rv-field-row">
            <label>Société</label>
            <input value={form.societe} onChange={e => set('societe', e.target.value)} placeholder="Nom de la société" />
          </div>
          <div className="rv-field-row">
            <label>Concession</label>
            <select value={form.concession_id} onChange={e => set('concession_id', e.target.value)}>
              <option value="">— Aucune —</option>
              {concessions.map(c => (
                <option key={c.id} value={c.id}>{c.nom}</option>
              ))}
            </select>
          </div>
          {isEdit && (
            <div className="rv-field-row" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                id="client-actif"
                checked={form.actif}
                onChange={e => set('actif', e.target.checked)}
                style={{ width: 'auto' }}
              />
              <label htmlFor="client-actif" style={{ marginBottom: 0, cursor: 'pointer' }}>Compte actif</label>
            </div>
          )}

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>Annuler</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Enregistrement…' : (isEdit ? 'Enregistrer' : 'Créer le client')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Composant principal ──────────────────────── */
function ReceptionVip({ user, onOpenVehicule = () => {}, editMode = false, setHeaderAction }) {
  const [view, setView] = useState('aujourd_hui');
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selectedDossier, setSelectedDossier] = useState(null);
  const [filters, setFilters] = useState({ client: '', statut: '', concession_id: '' });

  const [lastRefresh, setLastRefresh] = useState(null);
  const [concessions, setConcessions] = useState([]);

  // Clients VIP state
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClientIds, setSelectedClientIds] = useState([]);

  const isAdmin = ['super-admin', 'admin'].includes(user.role);
  const isSuperAdmin = user.role === 'super-admin';

  // Colonne Planification : le dossier du Planning Atelier s'ouvre ici meme,
  // et sa modale porte un bouton pour rejoindre la grille si besoin.
  const { ouvrir: ouvrirDossier, modale: modaleDossier } = useDossierPlanning({
    user, editMode, onEnregistre: () => loadDossiers(),
  });

  // Charger la liste des concessions (pour admin/super-admin)
  useEffect(() => {
    if (!isAdmin) return;
    const load = async () => {
      try {
        const resp = await api.get('/concessions');
        setConcessions(resp.data.concessions || resp.data || []);
      } catch { /* ignore */ }
    };
    load();
  }, [isAdmin]);

  // Charger les clients VIP
  const fetchClients = useCallback(async () => {
    if (!isAdmin) return;
    setClientsLoading(true);
    try {
      const params = {};
      if (clientSearch) params.search = clientSearch;
      const resp = await api.get('/reception-vip/clients', { params });
      setClients(resp.data.clients || []);
    } catch (err) {
      console.error('[ReceptionVip] Erreur chargement clients:', err);
    } finally {
      setClientsLoading(false);
    }
  }, [isAdmin, clientSearch]);

  useEffect(() => {
    if (view === 'clients') fetchClients();
  }, [view, fetchClients]);

  const fetchDossiers = useCallback(async (silent = false) => {
    if (view === 'clients') return;
    if (!silent) setLoading(true);
    setLoadError('');
    try {
      const params = {};
      if (filters.client) params.client = filters.client;
      if (filters.concession_id) params.concession_id = filters.concession_id;
      if (view === 'archives') {
        params.archives = 'true';
      } else if (filters.statut) {
        params.statut = filters.statut;
      }
      const resp = await api.get('/reception-vip', { params });
      setDossiers(resp.data.dossiers || []);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('[ReceptionVip] Erreur chargement:', err);
      if (!silent) setLoadError(err.response?.data?.error || err.message || 'Erreur de chargement');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [filters, view]);

  const loadDossiers = useCallback(() => fetchDossiers(false), [fetchDossiers]);

  // Chargement initial + rechargement sur changement de vue/filtre
  useEffect(() => {
    if (view !== 'clients') fetchDossiers(false);
  }, [fetchDossiers, view]);

  // Polling silencieux toutes les 30 secondes
  useEffect(() => {
    if (view === 'clients') return;
    const interval = setInterval(() => fetchDossiers(true), 30000);
    return () => clearInterval(interval);
  }, [fetchDossiers, view]);

  const todayStr = new Date().toISOString().split('T')[0];
  const isPlanifie = (d) => d.date_prochaine_action && d.date_prochaine_action.split('T')[0] > todayStr;
  const displayedDossiers = view === 'aujourd_hui' ? dossiers.filter(d => !isPlanifie(d)) : dossiers;
  const countAujourdhui = dossiers.filter(d => !isPlanifie(d)).length;

  const [showCreate, setShowCreate] = useState(false);
  const [mecaPopover, setMecaPopover] = useState(null);
  const [mecaPopoverPos, setMecaPopoverPos] = useState({ top: 0, right: 0 });

  // Boutons d'action dans le header
  useEffect(() => {
    if (!setHeaderAction) return;
    if (view === 'clients') {
      setHeaderAction({
        primary: { label: '+ Nouveau client', onClick: () => { setEditingClient(null); setShowClientModal(true); } },
      });
    } else {
      setHeaderAction({
        primary: { label: '+ Nouveau dossier', onClick: () => setShowCreate(true) },
      });
    }
  }, [setHeaderAction, view]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreated = (newDossier) => {
    setDossiers(prev => [newDossier, ...prev]);
    if (view === 'archives') setView('actifs');
  };

  const handleSaved = (updated) => {
    if (updated.statut === 'livre' && view !== 'archives') {
      setDossiers(prev => prev.filter(d => d.id !== updated.id));
      setSelectedDossier(null);
    } else {
      setDossiers(prev => prev.map(d => d.id === updated.id ? { ...d, ...updated } : d));
      setSelectedDossier(prev => prev ? { ...prev, ...updated } : null);
    }
  };

  const handleDeleted = (id) => {
    setDossiers(prev => prev.filter(d => d.id !== id));
    setSelectedDossier(null);
  };

  const handleUpdateStatutInline = async (id, newStatut) => {
    try {
      await api.put(`/reception-vip/${id}`, { statut: newStatut });
      handleSaved({ id, statut: newStatut });
    } catch (err) {
      console.error('[reception-vip] statut inline error:', err);
    }
  };

  const [numOrEdit, setNumOrEdit] = useState(null);
  const [numOrVal, setNumOrVal] = useState('');
  const [orModalId, setOrModalId] = useState(null);
  const [factureDetail, setFactureDetail] = useState(null);
  const [showFactureDetail, setShowFactureDetail] = useState(false);

  const handleSaveNumOr = async (id, val) => {
    setNumOrEdit(null);
    const trimmed = val.trim() || null;
    try {
      await api.patch(`/reception-vip/${id}/num-or`, { num_or: trimmed });
      setDossiers(prev => prev.map(d => d.id === id ? { ...d, num_or: trimmed } : d));
    } catch (err) {
      console.error('[reception-vip] num-or error:', err);
    }
  };

  const handleOpenFacture = async (factureId) => {
    try {
      const res = await api.get(`/factures-services/by-id/${factureId}`);
      setFactureDetail(res.data.facture);
      setShowFactureDetail(true);
    } catch (err) {
      console.error('[reception-vip] facture error:', err);
    }
  };

  const handleDeleteRow = async (e, id, label) => {
    e.stopPropagation();
    if (!window.confirm(`Supprimer définitivement le dossier #${id} (${label}) ?`)) return;
    try {
      await api.delete(`/reception-vip/${id}`);
      handleDeleted(id);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const handleView = (v) => {
    setView(v);
    if (v !== 'clients') {
      setFilters({ client: '', statut: '', concession_id: '' });
    }
  };

  // Client VIP handlers
  const handleClientSaved = (client, isEdit) => {
    if (isEdit) {
      setClients(prev => prev.map(c => c.id === client.id ? { ...c, ...client } : c));
    } else {
      setClients(prev => [client, ...prev]);
    }
  };

  const handleDeleteClients = async () => {
    if (selectedClientIds.length === 0) return;
    const count = selectedClientIds.length;
    if (!window.confirm(`Désactiver ${count} client(s) sélectionné(s) ?`)) return;
    try {
      for (const id of selectedClientIds) {
        await api.delete(`/reception-vip/clients/${id}`);
      }
      setClients(prev => prev.map(c =>
        selectedClientIds.includes(c.id) ? { ...c, actif: false } : c
      ));
      setSelectedClientIds([]);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la désactivation');
    }
  };

  const toggleClientSelection = (id) => {
    setSelectedClientIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleAllClients = () => {
    if (selectedClientIds.length === filteredClients.length) {
      setSelectedClientIds([]);
    } else {
      setSelectedClientIds(filteredClients.map(c => c.id));
    }
  };

  const filteredClients = clients.filter(c => {
    if (!clientSearch) return true;
    const s = clientSearch.toLowerCase();
    return (c.username || '').toLowerCase().includes(s)
      || (c.nom || '').toLowerCase().includes(s)
      || (c.prenom || '').toLowerCase().includes(s);
  });

  const STATUTS_ACTIFS = STATUTS.filter(s => s.valeur !== 'livre');

  return (
    <div className="page-container">
      {/* Onglets — toujours en premier */}
      <div className="page-tabs">
        <button
          className={view === 'aujourd_hui' ? 'active' : ''}
          onClick={() => handleView('aujourd_hui')}
        >
          📋 À traiter
          <span className="tab-count">{countAujourdhui}</span>
        </button>
        <button
          className={view === 'actifs' ? 'active' : ''}
          onClick={() => handleView('actifs')}
        >
          🚗 Tous les dossiers
          <span className="tab-count">{dossiers.length}</span>
        </button>
        <button
          className={view === 'archives' ? 'active' : ''}
          onClick={() => handleView('archives')}
        >
          🗄️ Archives
        </button>
        {isAdmin && (
          <button
            className={view === 'clients' ? 'active' : ''}
            onClick={() => handleView('clients')}
          >
            👤 Clients VIP
            <span className="tab-count">{clients.length}</span>
          </button>
        )}
      </div>

      {/* ═══════════ VUE CLIENTS VIP ═══════════ */}
      {view === 'clients' ? (
        <div>
          <div className="page-toolbar">
            <input
              type="text"
              className="page-search"
              placeholder="🔍 Rechercher..."
              value={clientSearch}
              onChange={e => setClientSearch(e.target.value)}
            />
            {editMode && isSuperAdmin && selectedClientIds.length > 0 && (
              <button className="btn-danger" onClick={handleDeleteClients} style={{ marginLeft: 'auto' }}>
                🗑 Désactiver la sélection ({selectedClientIds.length})
              </button>
            )}
          </div>

          {clientsLoading ? (
            <div className="rv-loading">Chargement…</div>
          ) : filteredClients.length === 0 ? (
            <div className="rv-empty">
              <div className="rv-empty-icon">👤</div>
              <p>Aucun client VIP enregistré</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="rv-table">
                <thead>
                  <tr>
                    {editMode && isSuperAdmin && (
                      <th style={{ width: 36 }}>
                        <input
                          type="checkbox"
                          checked={selectedClientIds.length === filteredClients.length && filteredClients.length > 0}
                          onChange={toggleAllClients}
                        />
                      </th>
                    )}
                    <th>Identifiant</th>
                    <th>Société</th>
                    <th>Nom</th>
                    <th>Prénom</th>
                    {isAdmin && <th>Concession</th>}
                    <th>Email</th>
                    <th>Téléphone</th>
                    <th>Statut</th>
                    <th>Créé le</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map(c => (
                    <tr key={c.id}>
                      {editMode && isSuperAdmin && (
                        <td onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedClientIds.includes(c.id)}
                            onChange={() => toggleClientSelection(c.id)}
                          />
                        </td>
                      )}
                      <td style={{ fontWeight: 600, fontFamily: 'monospace' }}>{c.username}</td>
                      <td style={{ fontWeight: 600 }}>{c.societe || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                      <td style={{ fontWeight: 600 }}>{c.nom}</td>
                      <td>{c.prenom || '—'}</td>
                      {isAdmin && <td>{c.concession_nom || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>}
                      <td style={{ color: 'var(--text-secondary)' }}>{c.email || '—'}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>{c.telephone || '—'}</td>
                      <td>
                        <span
                          className="statut-badge"
                          style={{ background: c.actif ? '#10b981' : '#ef4444' }}
                        >
                          {c.actif ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      <td style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        {c.created_at ? new Date(c.created_at).toLocaleDateString('fr-FR') : '—'}
                      </td>
                      <td>
                        <button
                          className="btn-secondary"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => { setEditingClient(c); setShowClientModal(true); }}
                        >
                          ✏️ Modifier
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {showClientModal && (
            <ClientModal
              client={editingClient}
              concessions={concessions}
              onClose={() => { setShowClientModal(false); setEditingClient(null); }}
              onSaved={handleClientSaved}
            />
          )}
        </div>
      ) : (
        <>
          {/* ═══════════ VUE DOSSIERS ═══════════ */}

          {/* Filtres */}
          <div className="page-toolbar">
            <input
              type="text"
              className="page-search"
              placeholder="🔍 Rechercher..."
              value={filters.client}
              onChange={e => setFilters(f => ({ ...f, client: e.target.value }))}
            />
            {view !== 'archives' && (
              <select
                className={`page-select${filters.statut ? ' active-filter' : ''}`}
                value={filters.statut}
                onChange={e => setFilters(f => ({ ...f, statut: e.target.value }))}
              >
                <option value="">Tous les statuts</option>
                {STATUTS_ACTIFS.map(s => (
                  <option key={s.valeur} value={s.valeur}>{s.label}</option>
                ))}
              </select>
            )}
            {isAdmin && concessions.length > 1 && (
              <select
                className={`page-select${filters.concession_id ? ' active-filter' : ''}`}
                value={filters.concession_id}
                onChange={e => setFilters(f => ({ ...f, concession_id: e.target.value }))}
              >
                <option value="">Toutes les concessions</option>
                {concessions.map(c => (
                  <option key={c.id} value={c.id}>{c.nom}</option>
                ))}
              </select>
            )}
          </div>

          {/* Erreur chargement */}
          {loadError && (
            <div style={{ background: '#fee2e2', color: 'var(--color-error-dark)', border: '1px solid #fca5a5', borderRadius: '8px', padding: '12px 16px', margin: '0 0 12px', fontSize: '13px', fontWeight: 600 }}>
              ❌ Erreur : {loadError}
            </div>
          )}

          {/* Contenu */}
          {loading ? (
            <div className="rv-loading">Chargement…</div>
          ) : displayedDossiers.length === 0 ? (
            <div className="rv-empty">
              <div className="rv-empty-icon">{view === 'archives' ? '📁' : '🏁'}</div>
              <p>
                {view === 'archives' ? 'Aucun véhicule livré en archives'
                  : view === 'aujourd_hui' ? '✅ Aucun dossier à traiter aujourd\'hui'
                  : 'Aucun dossier VIP en cours'}
              </p>
            </div>
          ) : (
            <div className="table-container">
              <table className="rv-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Client</th>
                    {isAdmin && <th>Concession</th>}
                    <th>Véhicule</th>
                    <th>Statut</th>
                    <th>Dépôt</th>
                    <th>Restitution</th>
                    <th>N° OR</th>
                    <th>Facture</th>
                    {isSuperAdmin && <th>Planification</th>}
                    <th>Prochaine action</th>
                    {isSuperAdmin && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {displayedDossiers.map(d => {
                    const daStr = d.date_prochaine_action ? d.date_prochaine_action.split('T')[0] : null;
                    const isLate   = daStr && daStr < todayStr;
                    const isToday  = daStr && daStr === todayStr;
                    const isFuture = daStr && daStr > todayStr;
                    return (
                    <tr key={d.id} onClick={() => setSelectedDossier(d)}>
                      <td style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{d.id}</td>
                      <td style={{ fontWeight: 600 }}>
                        {d.client_societe || (d.client_nom ? `${d.client_nom}${d.client_prenom ? ` ${d.client_prenom}` : ''}` : d.client_username)}
                      </td>
                      {isAdmin && (
                        <td style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                          {d.concession_nom || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                        </td>
                      )}
                      <td onClick={e => { e.stopPropagation(); if (d.plaque) onOpenVehicule(d.plaque); }}>
                        {d.plaque
                          ? <div>
                              <span className="plaque-badge plaque-badge-link" title="Voir la fiche véhicule">{d.plaque}</span>
                              {d.marque && <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px', fontWeight: '500' }}>{d.marque}</div>}
                            </div>
                          : <span style={{ color: 'var(--text-muted)' }}>—</span>
                        }
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <InlineStatusSelect
                          value={d.statut}
                          options={STATUTS.map(s => ({ value: s.valeur, label: s.label, color: s.couleur }))}
                          onChange={newStatut => handleUpdateStatutInline(d.id, newStatut)}
                        />
                      </td>
                      <td style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        {d.created_at ? new Date(d.created_at).toLocaleDateString('fr-FR') : '—'}
                      </td>
                      <td style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        {d.date_restitution
                          ? new Date(d.date_restitution).toLocaleDateString('fr-FR')
                          : <span style={{ color: 'var(--text-muted)' }}>—</span>
                        }
                      </td>
                      <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap', minWidth: '90px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                          {numOrEdit === d.id ? (
                            <input
                              className="rv-num-or-input"
                              autoFocus
                              value={numOrVal}
                              onChange={e => setNumOrVal(e.target.value)}
                              onBlur={() => handleSaveNumOr(d.id, numOrVal)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') handleSaveNumOr(d.id, numOrVal);
                                if (e.key === 'Escape') setNumOrEdit(null);
                              }}
                            />
                          ) : (
                            <span
                              className={d.num_or ? 'rv-num-or-value' : 'rv-num-or-empty'}
                              onClick={() => { setNumOrEdit(d.id); setNumOrVal(d.num_or || ''); }}
                              title="Cliquer pour modifier"
                            >
                              {d.num_or || '—'}
                            </span>
                          )}
                          {d.or_en_cours_id && (
                            <span
                              className="rv-badge-or"
                              title="Voir l'OR en cours"
                              onClick={() => setOrModalId(d.or_en_cours_id)}
                            >OR</span>
                          )}
                        </div>
                      </td>
                      <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                          <span>{d.facture_date ? new Date(d.facture_date).toLocaleDateString('fr-FR') : <span style={{ color: 'var(--text-muted)' }}>—</span>}</span>
                          {d.facture_id && (
                            <span
                              className="rv-badge-fac"
                              title="Voir la facture"
                              onClick={() => handleOpenFacture(d.facture_id)}
                            >FAC</span>
                          )}
                        </div>
                      </td>
                      {isSuperAdmin && (
                        <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                          <PlanificationCell dossier={d} onOuvrir={ouvrirDossier} />
                        </td>
                      )}
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {isLate   && <span className="date-action-badge late">⚠️ {new Date(d.date_prochaine_action).toLocaleDateString('fr-FR')}</span>}
                        {isToday  && <span className="date-action-badge today">📌 Aujourd'hui</span>}
                        {isFuture && <span className="date-action-badge future">📅 {new Date(d.date_prochaine_action).toLocaleDateString('fr-FR')}</span>}
                        {!daStr   && <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>
                      {isSuperAdmin && (
                        <td onClick={e => e.stopPropagation()} style={{ textAlign: 'center', padding: '0 8px' }}>
                          <button
                            className="btn-icon delete"
                            onClick={e => handleDeleteRow(e, d.id, d.client_username)}
                            title="Supprimer"
                          >🗑️</button>
                        </td>
                      )}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {showCreate && (
            <CreateModal onClose={() => setShowCreate(false)} onCreated={handleCreated} />
          )}

          {selectedDossier && (
            <DossierModal
              dossier={selectedDossier}
              onClose={() => setSelectedDossier(null)}
              onSaved={handleSaved}
              onDeleted={handleDeleted}
              isSuperAdmin={isSuperAdmin}
            />
          )}

          {orModalId && (
            <ORModal
              orId={orModalId}
              user={user}
              onClose={() => setOrModalId(null)}
            />
          )}

          {showFactureDetail && factureDetail && (
            <div
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
              onClick={() => setShowFactureDetail(false)}
            >
              <div
                style={{ background: '#fff', borderRadius: '10px', width: '100%', maxWidth: '780px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                onClick={e => e.stopPropagation()}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border-default)' }}>
                  <div>
                    <span style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)' }}>🧾 {factureDetail.num_facture}</span>
                    <span style={{ marginLeft: '12px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                      {(() => { const d = factureDetail.date_facture; if (!d) return '—'; const [y,m,day] = d.split('T')[0].split('-').map(Number); return new Date(y, m-1, day).toLocaleDateString('fr-FR'); })()} · OR : {factureDetail.num_or || '—'}
                    </span>
                  </div>
                  <button onClick={() => setShowFactureDetail(false)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: 'var(--text-secondary)' }}>×</button>
                </div>

                <div style={{ padding: '16px 20px', overflowY: 'auto' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '10px', marginBottom: '16px' }}>
                    {[
                      { label: 'Client',           value: factureDetail.nom_client },
                      { label: 'Immatriculation',  value: factureDetail.immat },
                      { label: 'VIN',              value: factureDetail.vin },
                      { label: 'Marque',           value: factureDetail.marque },
                      { label: 'Société',          value: factureDetail.societe },
                      { label: 'Département',      value: factureDetail.department },
                      { label: 'Réceptionnaire',   value: factureDetail.receptionnaire },
                      { label: 'Ouverture OR',     value: (() => { const d = factureDetail.date_ouverture; if (!d) return '—'; const [y,m,day] = d.split('T')[0].split('-').map(Number); return new Date(y, m-1, day).toLocaleDateString('fr-FR'); })() },
                      { label: 'Date facture',     value: (() => { const d = factureDetail.date_facture; if (!d) return '—'; const [y,m,day] = d.split('T')[0].split('-').map(Number); return new Date(y, m-1, day).toLocaleDateString('fr-FR'); })() },
                      { label: 'Montant HT total', value: new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(factureDetail.montant_ht), highlight: true },
                    ].map(item => (
                      <div key={item.label} style={{ background: 'var(--bg-muted)', borderRadius: '6px', padding: '10px 12px' }}>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '3px' }}>{item.label}</div>
                        <div style={{ fontWeight: item.highlight ? 700 : 500, color: item.highlight ? '#059669' : '#111827', fontSize: item.highlight ? '15px' : '13px' }}>
                          {item.value || '—'}
                        </div>
                      </div>
                    ))}
                  </div>

                  {factureDetail.lignes_json && factureDetail.lignes_json.length > 0 && (
                    <>
                      <h3 style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>
                        Lignes ({factureDetail.lignes_json.length})
                      </h3>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                        <thead>
                          <tr style={{ background: '#f3f4f6' }}>
                            {['Prestation', 'Désignation', 'Réf.', 'Qté', 'Montant HT'].map(h => (
                              <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-primary)' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {factureDetail.lignes_json.map((l, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                              <td style={{ padding: '6px 10px' }}>
                                <span style={{
                                  background: l.prestation === 'M.O.' ? '#fef3c7' : '#eff6ff',
                                  color:      l.prestation === 'M.O.' ? '#92400e' : '#1d4ed8',
                                  padding: '1px 6px', borderRadius: '4px', fontWeight: 600, fontSize: '11px',
                                }}>{l.prestation || '—'}</span>
                              </td>
                              <td style={{ padding: '6px 10px', color: 'var(--text-primary)' }}>{l.designation || '—'}</td>
                              <td style={{ padding: '6px 10px', fontFamily: 'monospace', color: 'var(--text-secondary)', fontSize: '11px' }}>{l.ref || '—'}</td>
                              <td style={{ padding: '6px 10px', textAlign: 'right' }}>{l.qte != null ? l.qte : '—'}</td>
                              <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: parseFloat(l.montant_ht) < 0 ? '#dc2626' : '#059669' }}>
                                {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(l.montant_ht)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: 'var(--bg-muted)', borderTop: '2px solid var(--border-default)' }}>
                            <td colSpan={4} style={{ padding: '8px 10px', fontWeight: 700, textAlign: 'right', fontSize: '13px' }}>Total HT</td>
                            <td style={{ padding: '8px 10px', fontWeight: 700, fontSize: '14px', textAlign: 'right', color: parseFloat(factureDetail.montant_ht) < 0 ? '#dc2626' : '#059669' }}>
                              {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(factureDetail.montant_ht)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </>
                  )}
                </div>

                <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', textAlign: 'right' }}>
                  <button
                    onClick={() => setShowFactureDetail(false)}
                    style={{ padding: '8px 20px', background: '#e5e7eb', color: 'var(--text-primary)', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      {modaleDossier}
    </div>
  );
}

export default ReceptionVip;
